import React, { useState, useEffect } from 'react';
import { ArrowLeftRight, Plus, Truck, CheckCircle2, Clock, MapPin, Search, AlertCircle } from 'lucide-react';
import type { StockTransferStatus } from '@zarin/types';
import { apiFetch } from '../lib/auth';

interface TransferDisplay {
  id: string;
  transferNumber: string;
  sourceLocation: string;
  destinationLocation: string;
  status: StockTransferStatus;
  trackingNumber?: string | undefined;
  notes?: string | undefined;
  itemCount: number;
  totalQuantity: number;
  createdAt: string;
}

const INITIAL_TRANSFERS: TransferDisplay[] = [];

const LOCATIONS = [
  { id: 'loc_1', name: 'Bhiwandi Central DC', code: 'DC-BHI', type: 'Fulfillment Hub', city: 'Bhiwandi' },
  { id: 'loc_2', name: 'Kala Ghoda Flagship Store', code: 'STORE-BOM1', type: 'Retail Store', city: 'Mumbai' },
  { id: 'loc_3', name: 'Khan Market Boutique', code: 'STORE-DEL1', type: 'Retail Store', city: 'New Delhi' },
];

export default function TransfersPage() {
  const [transfers, setTransfers] = useState<TransferDisplay[]>(INITIAL_TRANSFERS);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    async function loadTransfers() {
      try {
        setLoading(true);
        const res = await apiFetch<{ data: any[] }>('/transfers');
        if (Array.isArray(res.data)) {
          setTransfers(res.data.map((t: any) => ({
            id: t.id,
            transferNumber: t.transfer_number || t.transferNumber || `TR-${t.id.slice(0, 6)}`,
            sourceLocation: t.source_location_name || t.sourceLocationName || t.source_location || t.sourceLocation || 'DC Bhiwandi',
            destinationLocation: t.destination_location_name || t.destinationLocationName || t.destination_location || t.destinationLocation || 'Flagship Store',
            status: t.status || 'draft',
            trackingNumber: t.tracking_number || t.trackingNumber,
            notes: t.notes,
            itemCount: t.item_count || t.itemCount || 0,
            totalQuantity: t.total_quantity || t.totalQuantity || 0,
            createdAt: t.created_at || t.createdAt || new Date().toISOString(),
          })));
        }
      } catch (err) {
        console.error('Failed to load transfers:', err);
      } finally {
        setLoading(false);
      }
    }
    loadTransfers();
  }, []);

  // Form states
  const [source, setSource] = useState('loc_1');
  const [dest, setDest] = useState('loc_2');
  const [tracking, setTracking] = useState('');
  const [qty, setQty] = useState('20');
  const [notes, setNotes] = useState('');

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const srcName = LOCATIONS.find(l => l.id === source)?.name || 'Central Hub';
    const destName = LOCATIONS.find(l => l.id === dest)?.name || 'Flagship';

    try {
      const res = await apiFetch<{ data: any }>('/transfers', {
        method: 'POST',
        body: JSON.stringify({
          sourceLocationId: source,
          destinationLocationId: dest,
          trackingNumber: tracking.trim() || null,
          notes: notes.trim() || null,
          items: [{ variantId: 'var_default', quantityExpected: Number(qty) || 10 }],
        }),
      });
      const created = res.data;
      const newTransfer: TransferDisplay = {
        id: created?.id || `tr_${Date.now()}`,
        transferNumber: created?.transfer_number || created?.transferNumber || `TR-${1080 + transfers.length + 1}`,
        sourceLocation: srcName,
        destinationLocation: destName,
        status: 'draft',
        trackingNumber: tracking.trim() || undefined,
        notes: notes.trim() || undefined,
        itemCount: 1,
        totalQuantity: Number(qty) || 10,
        createdAt: created?.created_at || new Date().toISOString(),
      };
      setTransfers([newTransfer, ...transfers]);
    } catch (err) {
      console.error('Failed to persist transfer to API:', err);
      const newTransfer: TransferDisplay = {
        id: `tr_${Date.now()}`,
        transferNumber: `TR-${1080 + transfers.length + 1}`,
        sourceLocation: srcName,
        destinationLocation: destName,
        status: 'draft',
        trackingNumber: tracking.trim() || undefined,
        notes: notes.trim() || undefined,
        itemCount: 1,
        totalQuantity: Number(qty) || 10,
        createdAt: new Date().toISOString(),
      };
      setTransfers([newTransfer, ...transfers]);
    }
    setShowModal(false);
    setTracking('');
    setNotes('');
  };

  const updateStatus = async (id: string, newStatus: StockTransferStatus) => {
    setTransfers(prev => prev.map(t => t.id === id ? { ...t, status: newStatus } : t));
    try {
      if (newStatus === 'in_transit') {
        await apiFetch(`/transfers/${id}/ship`, { method: 'POST' });
      } else if (newStatus === 'completed') {
        await apiFetch(`/transfers/${id}/receive`, { method: 'POST', body: JSON.stringify({}) });
      }
    } catch (err) {
      console.error(`Failed to update transfer status to ${newStatus}:`, err);
    }
  };

  const filteredTransfers = transfers.filter(t =>
    t.transferNumber.toLowerCase().includes(search.toLowerCase()) ||
    t.sourceLocation.toLowerCase().includes(search.toLowerCase()) ||
    t.destinationLocation.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Multi-Location Transfers</h1>
          <p style={styles.subtitle}>Coordinate inter-store and fulfillment center inventory movements</p>
        </div>
        <button style={styles.btnPrimary} onClick={() => setShowModal(true)}>
          <Plus size={16} />
          <span>New Stock Transfer</span>
        </button>
      </div>

      {/* Locations Summary Banner */}
      <div style={styles.locationsRow}>
        {LOCATIONS.map(loc => (
          <div key={loc.id} style={styles.locCard}>
            <div style={styles.locHeader}>
              <MapPin size={14} color="#C9A84C" />
              <span style={styles.locCode}>{loc.code}</span>
            </div>
            <div style={styles.locName}>{loc.name}</div>
            <div style={styles.locCity}>{loc.type} • {loc.city}</div>
          </div>
        ))}
      </div>

      {/* Search Toolbar */}
      <div style={styles.toolbar}>
        <div style={styles.searchBox}>
          <Search size={16} color="#888" />
          <input
            type="text"
            placeholder="Search transfer number, origin or destination..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
          />
        </div>
      </div>

      {/* Transfers Table */}
      <div style={styles.tableCard}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Transfer #</th>
              <th style={styles.th}>Origin & Destination</th>
              <th style={styles.th}>Items / Total Qty</th>
              <th style={styles.th}>Tracking</th>
              <th style={styles.th}>Status</th>
              <th style={styles.thRight}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransfers.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: '48px 24px', textAlign: 'center', color: '#8A8A8E' }}>
                  {loading ? 'Loading stock transfers…' : 'No stock transfers found. Click "New Transfer" above to initiate warehouse transfers.'}
                </td>
              </tr>
            ) : (
              filteredTransfers.map((tr) => (
                <tr key={tr.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={{ fontWeight: 600, color: '#FFF' }}>{tr.transferNumber}</div>
                    <div style={{ fontSize: 11, color: '#71717A' }}>
                      {new Date(tr.createdAt).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
                    </div>
                  </td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: '#D4D4D8' }}>{tr.sourceLocation}</span>
                      <ArrowLeftRight size={13} color="#C9A84C" />
                      <span style={{ color: '#FFF', fontWeight: 500 }}>{tr.destinationLocation}</span>
                    </div>
                    {tr.notes && <div style={{ fontSize: 11, color: '#888', marginTop: 3 }}>Note: {tr.notes}</div>}
                  </td>
                  <td style={styles.td}>
                    <div style={{ color: '#FFF' }}>{tr.totalQuantity} units</div>
                    <div style={{ fontSize: 11, color: '#888' }}>{tr.itemCount} SKUs</div>
                  </td>
                  <td style={styles.td}>
                    <code style={styles.trackingBadge}>{tr.trackingNumber || 'Pending Dispatch'}</code>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.statusBadge(tr.status)}>{tr.status.replace('_', ' ').toUpperCase()}</span>
                  </td>
                  <td style={styles.tdRight}>
                    {tr.status === 'draft' && (
                      <button
                        onClick={() => updateStatus(tr.id, 'in_transit')}
                        style={{ ...styles.actionBtn, ...styles.dispatchBtn }}
                        title="Mark as shipped"
                      >
                        <Truck size={13} /> Dispatch
                      </button>
                    )}
                    {tr.status === 'in_transit' && (
                      <button
                        onClick={() => updateStatus(tr.id, 'completed')}
                        style={{ ...styles.actionBtn, ...styles.receiveBtn }}
                        title="Receive items and update on-hand inventory"
                      >
                        <CheckCircle2 size={13} /> Receive
                      </button>
                    )}
                    {tr.status === 'completed' && (
                      <span style={{ fontSize: 12, color: '#10B981', fontWeight: 500 }}>Delivered & Synced</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div style={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Initiate Stock Transfer</h3>
            <p style={styles.modalSubtitle}>Select origin warehouse, target destination, and inventory quantities</p>

            <form onSubmit={handleCreate} style={styles.form}>
              <div style={styles.field}>
                <label style={styles.label}>Source Location</label>
                <select value={source} onChange={(e) => setSource(e.target.value)} style={styles.select}>
                  {LOCATIONS.map(l => (
                    <option key={l.id} value={l.id}>{l.name} ({l.code})</option>
                  ))}
                </select>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Destination Location</label>
                <select value={dest} onChange={(e) => setDest(e.target.value)} style={styles.select}>
                  {LOCATIONS.map(l => (
                    <option key={l.id} value={l.id} disabled={l.id === source}>{l.name} ({l.code})</option>
                  ))}
                </select>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Transfer Quantity (Units)</label>
                <input
                  type="number"
                  min="1"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Logistics Courier / Waybill (Optional)</label>
                <input
                  type="text"
                  value={tracking}
                  onChange={(e) => setTracking(e.target.value)}
                  style={styles.input}
                  placeholder="e.g. DELHIVERY-198273"
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Internal Transfer Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  style={{ ...styles.input, height: 60, resize: 'none' }}
                  placeholder="Purpose of transfer or batch instructions..."
                />
              </div>

              <div style={styles.modalActions}>
                <button type="button" onClick={() => setShowModal(false)} style={styles.cancelBtn}>
                  Cancel
                </button>
                <button type="submit" style={styles.submitBtn}>
                  Create Transfer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, any> = {
  page: {
    padding: '28px 32px',
    maxWidth: 1200,
    margin: '0 auto',
    color: '#E5E5E5',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 24,
  },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 26,
    fontWeight: 600,
    color: '#FFFFFF',
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: '#8A8A8E',
    marginTop: 6,
  },
  btnPrimary: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: '#C9A84C',
    color: '#0A0A0A',
    border: 'none',
    padding: '9px 16px',
    borderRadius: 6,
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
  },
  locationsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 16,
    marginBottom: 24,
  },
  locCard: {
    background: '#141414',
    border: '1px solid #262626',
    borderRadius: 8,
    padding: '16px 20px',
  },
  locHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  locCode: {
    fontSize: 11,
    background: '#222',
    color: '#C9A84C',
    padding: '2px 6px',
    borderRadius: 3,
    fontFamily: 'monospace',
  },
  locName: {
    fontSize: 15,
    fontWeight: 600,
    color: '#FFF',
    marginBottom: 4,
  },
  locCity: {
    fontSize: 12,
    color: '#8A8A8E',
  },
  toolbar: {
    marginBottom: 16,
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: '#141414',
    border: '1px solid #262626',
    borderRadius: 6,
    padding: '8px 14px',
    maxWidth: 440,
  },
  searchInput: {
    background: 'transparent',
    border: 'none',
    color: '#FFF',
    fontSize: 13,
    outline: 'none',
    width: '100%',
  },
  tableCard: {
    background: '#141414',
    border: '1px solid #262626',
    borderRadius: 8,
    overflow: 'hidden',
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left',
  },
  th: {
    padding: '12px 18px',
    background: '#1A1A1A',
    color: '#8A8A8E',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #262626',
  },
  thRight: {
    padding: '12px 18px',
    background: '#1A1A1A',
    color: '#8A8A8E',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #262626',
    textAlign: 'right',
  },
  tr: {
    borderBottom: '1px solid #1F1F1F',
  },
  td: {
    padding: '14px 18px',
    fontSize: 13,
    color: '#D4D4D8',
    verticalAlign: 'middle',
  },
  tdRight: {
    padding: '14px 18px',
    textAlign: 'right',
    verticalAlign: 'middle',
  },
  trackingBadge: {
    fontFamily: 'monospace',
    fontSize: 11,
    background: '#1F1F1F',
    color: '#A1A1AA',
    padding: '3px 8px',
    borderRadius: 4,
  },
  statusBadge: (status: StockTransferStatus) => ({
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.05em',
    padding: '2px 8px',
    borderRadius: 4,
    background:
      status === 'completed' ? 'rgba(16, 185, 129, 0.15)' :
      status === 'in_transit' ? 'rgba(201, 168, 76, 0.15)' :
      status === 'cancelled' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(113, 113, 122, 0.15)',
    color:
      status === 'completed' ? '#10B981' :
      status === 'in_transit' ? '#C9A84C' :
      status === 'cancelled' ? '#EF4444' : '#A1A1AA',
  }),
  actionBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    border: 'none',
    padding: '5px 12px',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
  },
  dispatchBtn: {
    background: 'rgba(201, 168, 76, 0.2)',
    color: '#C9A84C',
  },
  receiveBtn: {
    background: '#10B981',
    color: '#0A0A0A',
    fontWeight: 600,
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#141414',
    border: '1px solid #2A2A2A',
    borderRadius: 10,
    width: 480,
    maxWidth: '90vw',
    padding: 24,
  },
  modalTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 20,
    fontWeight: 600,
    color: '#FFF',
    margin: 0,
  },
  modalSubtitle: {
    fontSize: 12,
    color: '#8A8A8E',
    marginTop: 4,
    marginBottom: 20,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 12,
    fontWeight: 500,
    color: '#A1A1AA',
  },
  select: {
    background: '#1F1F1F',
    border: '1px solid #333',
    borderRadius: 6,
    padding: '8px 12px',
    color: '#FFF',
    fontSize: 13,
    outline: 'none',
  },
  input: {
    background: '#1F1F1F',
    border: '1px solid #333',
    borderRadius: 6,
    padding: '8px 12px',
    color: '#FFF',
    fontSize: 13,
    outline: 'none',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 10,
  },
  cancelBtn: {
    background: '#27272A',
    border: 'none',
    borderRadius: 6,
    padding: '8px 16px',
    color: '#CCC',
    fontSize: 13,
    cursor: 'pointer',
  },
  submitBtn: {
    background: '#C9A84C',
    border: 'none',
    borderRadius: 6,
    padding: '8px 18px',
    color: '#0A0A0A',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
  },
};
